import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PlessHttp } from 'src/app/shared/http/pless-http.service';
import { PlessRequest } from 'src/app/shared/vo/pless-request.model';
import { PlessJsonResponderSub } from 'src/app/commons/kymw00/pless-json-responder-sub.service';
import { PlessDialogServiceSub } from 'src/app/commons/kymw00/pless-dialog-service-sub';
import {
  KYMW7002Model,
  KYMW7002SelectionFlg,
  PARENT_PAGE_ID,
} from '../kymw7002.model';

import type { SelectedGoodsFlgResult } from '../vo/selected-goods-flg-result.model';
import type { GetSelectedGoodsFlgResultCondition } from '../vo/get-selected-goods-flg-result-condition.model';

// リクエスト構造
export interface SelectedGoodsFlgResultRequest {
  search: {
    SelectedGoodsFlgResult: {
      condition: GetSelectedGoodsFlgResultCondition;
    };
  };
}

// レスポンス構造
export interface SelectedGoodsFlgResultResponse {
  /** 受注コード */
  OrderCode: string;
  /** 媒体 */
  MediumType: string;
  /** 特集コード */
  FeatureCode: string;
  /** 特集選定依頼ID */
  FeatureRequestId: string;
  /** 商品選定済みフラグ */
  SelectionFlg: string;
}

/**
 * 商品選定済みフラグ確認結果 取得サービス
 * @export GetSelectedGoodsFlgResultService
 * @class GetSelectedGoodsFlgResultService
 * @extends {PlessJsonResponderSub}
 */
@Injectable()
export class GetSelectedGoodsFlgResultService extends PlessJsonResponderSub<SelectedGoodsFlgResultResponse> {
  model: KYMW7002Model;
  vo: any;
  private requestData: SelectedGoodsFlgResultRequest;
  private calbak: () => void;

  constructor(
    protected plessHttp: PlessHttp,
    protected dialogService: PlessDialogServiceSub,
    private router: Router,
  ) {
    super(plessHttp, dialogService);
  }

  /**
   * bodyにセットする値を返す
   *
   * @private
   * @returns {PlessRequest} リクエストJSON
   * @memberof GetSelectedGoodsFlgResultService
   */
  private getBody(): PlessRequest {
    // 検索条件を設定
    const condition: GetSelectedGoodsFlgResultCondition = {
      OrderCode: this.model.orderCode ?? '',
      MediumType: this.model.mediumType ?? '',
      FeatureCode: this.model.featureCode ?? '',
      UserType: this.vo.userType,
      PayeeSupplierCode: this.vo.payeeSupplierCode,
      AssociatedCompanyId: this.vo.associatedCompanyId,
    };

    if (condition.UserType === '1') {
      delete condition.AssociatedCompanyId;
    } else if (condition.UserType === '2') {
      delete condition.PayeeSupplierCode;
    }

    // リクエストを設定
    this.requestData = {
      search: {
        SelectedGoodsFlgResult: { condition }
      }
    };

    return new PlessRequest(this.requestData);
  }
  
  /**
   * API呼び出し
   *
   * @param model
   * @memberof GetSelectedGoodsFlgResultService
   */
  execute(model: KYMW7002Model, vo: any, f?: () => void): void {
    this.model = model;
    this.vo = vo;
    if (f) this.calbak = f;
    this.send('getSelectedGoodsFlgResult', this.getBody());
  }

  /**
   * API通信成功時の処理
   *
   * @param {*} data レスポンスデータ
   * @memberof GetSelectedGoodsFlgResultService
   */
  success(data: SelectedGoodsFlgResultResponse) {
    const selectionFlg = (data.SelectionFlg === '0') ? KYMW7002SelectionFlg.UNSELECTED
                         : (data.SelectionFlg === '1') ? KYMW7002SelectionFlg.DRAFT
                         : (data.SelectionFlg === '2') ? KYMW7002SelectionFlg.SELECTED
                         : KYMW7002SelectionFlg.UNSELECTED;

    this.model.selectedGoodsFlgResult = {
      OrderCode: data.OrderCode,
      MediumType: data.MediumType,
      FeatureCode: data.FeatureCode,
      FeatureRequestId: data.FeatureRequestId,
      SelectionFlg: selectionFlg,
    } as SelectedGoodsFlgResult;

    if (this.calbak) this.calbak();
  }

  /**
   * 業務エラー時のメソッド
   * @param errList サーバ受信データ
   * 機能独自の処理を行う場合、このメソッドをオーバーライドして処理を実装する。
   */
  plessErrorHandler(errList: Object): void {
    super.plessErrorHandler(errList);
    this.backParentPage();
  }

  /**
   * 特殊なエラーが返ってきた場合に呼び出されるメソッド。<br/>
   * 機能独自のステータスコードを持つ場合、このメソッドをオーバーライドして処理を実装する。
   */
  exceptionError(status: string, result: any): void {
    super.exceptionError(status, result);
    this.backParentPage();
  }

  /**
   * システムエラー時のメソッド
   * @param data サーバ受信データ
   *  機能独自の処理を行う場合、このメソッドをオーバーライドして処理を実装する。
   */
  systemErrorHandler(data: any): void {
    super.systemErrorHandler(data);
    this.backParentPage();
  }

  /**
   * リクエスト失敗時のメソッド。
   * 失敗時の処理を実装する場合、このメソッドをオーバーライドして処理を実装する。
   */
  fault() {
    super.fault();
    this.backParentPage();
  }

  /**
   * 親画面に戻る
   * @memberof KYMW7002Component
   */
  private backParentPage(): void {
    this.router.navigate([PARENT_PAGE_ID]);
  }

}
